import type { ResponseRead, ResponseReadAll } from "epsm-api-types";
import type {
	ScreenerData,
	ScreenerFilterData,
	ScreenerState,
} from "epsm-api-types/models";
import { useGetData } from "@/hooks/useGetData";
import type { IReadAllParams } from "@/services/RequestParams";
import { ScreenerDataService } from "./ScreenerDataService";
import { ScreenerFilterDataService } from "./ScreenerFilterDataService";
import { ScreenerStateService } from "./ScreenerStateService";

export interface ScreenerDataOptions {
	params: IReadAllParams;
}

export interface ScreenerFilterDataOptions {
	name: string;
	subField?: string;
}

export interface ScreenerStateOptions {
	userId: string;
	itemName: string;
	state: ScreenerState;
}

export class UseData {
	private constructor(
		private screenerDataService: ScreenerDataService,
		private screenerFilterDataService: ScreenerFilterDataService,
		private screenerStateService: ScreenerStateService,
	) {}

	private static instance: UseData;

	static create() {
		if (!UseData.instance) {
			UseData.instance = new UseData(
				ScreenerDataService.create(),
				ScreenerFilterDataService.create(),
				ScreenerStateService.create(),
			);
		}
		return UseData.instance;
	}

	getData = async ({
		params,
	}: Readonly<ScreenerDataOptions>): Promise<ResponseReadAll<ScreenerData>> => {
		return await this.screenerDataService.getData(params);
	};

	getFilterData = async ({
		name,
		subField,
	}: ScreenerFilterDataOptions): Promise<ScreenerFilterData> => {
		return await this.screenerFilterDataService.getData(name, subField);
	};

	addState = async ({ userId, itemName, state }: ScreenerStateOptions) => {
		await this.screenerStateService.addState(userId, itemName, state);
	};

	getStates = async ({
		userId,
	}: ScreenerStateOptions): Promise<ResponseReadAll<ScreenerState>> => {
		return await this.screenerStateService.getStates(userId);
	};

	getState = async ({
		userId,
		itemName,
	}: ScreenerStateOptions): Promise<ResponseRead<ScreenerState>> => {
		return await this.screenerStateService.getState(userId, itemName);
	};

	removeState = async ({ userId, itemName }: ScreenerStateOptions) => {
		await this.screenerStateService.removeState(userId, itemName);
	};

	useGetData = () =>
		useGetData<ResponseReadAll<ScreenerData>, Readonly<ScreenerDataOptions>>(
			this.getData,
		);

	useGetFilterData = () =>
		useGetData<ScreenerFilterData, ScreenerFilterDataOptions>(
			this.getFilterData,
		);
}
